import { commentSchema, CommentService, ICommentSchema } from '@/api/comment';
import { ITestIssueSchema } from '@/api/testIssue';
import { zodResolver } from '@hookform/resolvers/zod';
import { Button, Dialog } from 'alurkerja-ui';
import React from 'react';
import { useForm } from 'react-hook-form';
import { FormContainer } from '@/components/form/FormContainer';
import { SelectField } from '@/components/form/fields/SelectField';
import { TestIssueStatus } from '@/utils/constants/testIssue';
import { TextAreaField } from '@/components/form/fields/TextAreaField';
import { UploadField } from '@/components/form/fields/UploadField';
import { OutlineButton } from '@/components/shared/button/OutlineButton';
import { Section } from '@/components/Section';

type CommentFormProps = {
  testIssue: ITestIssueSchema;
  comment?: ICommentSchema;
  onSuccess?: () => void;
  closeModal?: () => void;
};

export const CommentForm: React.FC<CommentFormProps> = ({
  testIssue,
  comment,
  onSuccess,
  closeModal,
}) => {
  const { mutate: createComment, isPending: isCreateLoading } =
    CommentService.useCreate();
  const { mutate: updateComment, isPending: isUpdateLoading } =
    CommentService.useUpdate();
  const isLoading = isCreateLoading || isUpdateLoading;

  const form = useForm<ICommentSchema>({
    resolver: zodResolver(commentSchema),
    values: comment,
  });

  const successCallback = () => {
    onSuccess?.();

    closeModal!();
    form.reset();
  };

  const onSubmit = (data: ICommentSchema) => {
    data.test_issue_id = testIssue.id!;

    if (comment) {
      updateComment(data, {
        onSuccess: () => {
          Dialog.success({
            title: 'Success',
            description: 'Comment has been updated successfully',
            callback: successCallback,
          });
        },
        onError: (error: any) => {
          Dialog.error({
            title: 'Error',
            description:
              error?.response?.data?.message ?? 'Failed to update comment',
          });
        },
      });
    } else {
      createComment(data, {
        onSuccess: () => {
          Dialog.success({
            title: 'Success',
            description: 'Comment has been created successfully',
            callback: successCallback,
          });
        },
        onError: (error: any) => {
          Dialog.error({
            title: 'Error',
            description:
              error?.response?.data?.message ?? 'Failed to create comment',
          });
        },
      });
    }
  };

  return (
    <Section title="Add Comment">
      <div className="p-4">
        <FormContainer form={form} onSubmit={onSubmit}>
          <div className="flex gap-5">
            <SelectField
              options={TestIssueStatus.toOptions()}
              name="status"
              label="Status"
              hasRequiredAsterisk
              isSearchable
            />
          </div>

          <div>
            <TextAreaField
              name="comment_summary"
              label="Comments"
              placeholder="Enter your comment"
              hasRequiredAsterisk
            />
          </div>

          <div className="mt-3">
            <UploadField
              name="medias"
              note="File size should be less than 2MB and allowed formats are .xls, .xlsx, .doc, .txt, .pdf, .csv, .log, .zip, .mp4, .webm, .ipynb, .odf, .gif, .png, .jpg, .ppt, .pptx, .mov"
              type="file"
              isMultiple
            />
          </div>

          <div className="flex justify-end gap-2 mt-4">
            <Button loading={isLoading} type="submit">
              Save
            </Button>

            <OutlineButton onClick={closeModal}>Cancel</OutlineButton>
          </div>
        </FormContainer>
      </div>
    </Section>
  );
};
